import { myBookmarkedEntityIdsPrefix } from '@mdvip/shared/features/bookmarks/useMyBookmarkedEntityIds';
import { type BookmarkCacheUpdater, useToggleBookmark } from '@mdvip/shared/features/bookmarks/useToggleBookmark';
import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

type SavedIdsSnapshot = Array<{ queryKey: QueryKey; data: string[] }>;

const WELLNESS_SAVED_IDS = myBookmarkedEntityIdsPrefix('WELLNESS_ARTICLE');

/**
 * Toggling a wellness article's bookmark. `WellnessArticle` has no `isBookmarked` field, so the saved
 * state lives in the cached `myBookmarkedEntityIds` sets: every cached check that covered this article
 * is patched, so the featured card and a grid card showing the same article agree.
 */
export function useToggleWellnessArticleBookmark() {
  const queryClient = useQueryClient();

  const cache = useMemo<BookmarkCacheUpdater<SavedIdsSnapshot>>(
    () => ({
      apply(entityId, next) {
        const snapshot: SavedIdsSnapshot = [];
        for (const [queryKey, data] of queryClient.getQueriesData<string[]>({ queryKey: WELLNESS_SAVED_IDS })) {
          if (!data) continue;
          // queryKey[3] is the id set the check covered; a check that never asked about this article stays as it is.
          const covered = queryKey[3] as string[] | undefined;
          if (!covered?.includes(entityId)) continue;
          snapshot.push({ queryKey, data });
          const without = data.filter((id) => id !== entityId);
          queryClient.setQueryData<string[]>(queryKey, next ? [...without, entityId] : without);
        }
        return snapshot;
      },
      rollback(snapshot) {
        for (const { queryKey, data } of snapshot) queryClient.setQueryData(queryKey, data);
      },
      async invalidate() {
        await queryClient.invalidateQueries({ queryKey: WELLNESS_SAVED_IDS });
      },
    }),
    [queryClient],
  );

  return useToggleBookmark('WELLNESS_ARTICLE', cache);
}
